import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useLists } from './Lists';
import { useAuth } from '../contexts/AuthContext';
import { canAcquire, ACQUISITIONS_PATHS } from '../utils/roles';
import Walkthrough from './Walkthrough';
import NotificationBell from './NotificationBell';

// Non-blocking banner shown until the signed-in user verifies their email.
// Standing notice for a workspace whose sends are simulated (migration 088). Deliberately NOT
// dismissible: the walkthrough says this once on the way past, and after that the only thing keeping
// "I just emailed the listing agent" from being believed is this line. It is the cheapest possible
// insurance against the single worst outcome of a demo account — someone acting on a message they
// think went out.
function SimulatedSendsBanner() {
  const { org } = useAuth();
  if (!org?.simulated_outbound) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 9, marginBottom: 18,
      padding: '9px 13px', borderRadius: 9,
      background: 'rgba(251,191,36,0.09)', border: '1px solid rgba(251,191,36,0.34)',
      color: '#FBBF24', fontSize: 12.5,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 4, background: '#FBBF24', flexShrink: 0 }} />
      <span>
        <strong style={{ fontWeight: 700 }}>Demo workspace.</strong>{' '}
        MLS data, comps and AI analysis are real and live. Email, SMS and e-signature are simulated —
        messages are logged here but never delivered.
      </span>
    </div>
  );
}

function VerifyEmailBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  if (!user || user.email_verified || dismissed) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--gold-dim)', border: '1px solid rgba(224, 165, 38, 0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: 'var(--white)' }}>
      <span style={{ flex: 1 }}>
        Please verify your email{user.email ? <> — we sent a link to <strong>{user.email}</strong></> : ''}. You can keep working in the meantime.
      </span>
      <button onClick={() => setDismissed(true)} style={{ background: 'transparent', border: 'none', color: 'var(--gray-300)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }} aria-label="Dismiss">×</button>
    </div>
  );
}

const navItems = [
  { to: '/',                  label: 'Dashboard',         icon: <DashboardIcon /> },
  { to: '/pipeline',          label: 'Pipeline',          icon: <PipelineIcon /> },
  { to: '/deals',             label: 'Deals',             icon: <DealsIcon /> },
  { to: '/mls-opportunities', label: 'MLS Opportunities', icon: <MlsIcon /> },
  { to: '/off-market',        label: 'Off Market',        icon: <OffMarketIcon /> },
  { to: '/buyers',            label: 'Buyers Database',   icon: <BuyersIcon /> },
  { to: '/communications',    label: 'Communications',    icon: <CommsIcon /> },
  { to: '/settings',          label: 'Settings',          icon: <SettingsIcon /> },
];

// A sales agent's sidebar: no sourcing (MLS Opportunities, Off Market) and, in place of the
// acquisitions Deals tab, the deals they have sold. Same order otherwise.
function navFor(role) {
  if (canAcquire(role)) return navItems;
  return navItems.flatMap((item) => {
    if (item.to === '/deals') return [{ to: '/my-sales', label: 'My Sales', icon: <DealsIcon /> }];
    return ACQUISITIONS_PATHS.includes(item.to) ? [] : [item];
  });
}

const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', agent: 'Agent', acq_agent: 'Acquisitions', sales_agent: 'Sales' };

export default function Layout({ onLogout }) {

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar onLogout={onLogout} />
      <main style={{
        flex: 1,
        overflow: 'auto',
        background: 'var(--navy)',
        color: 'var(--white)',
        padding: '32px 36px',
      }}>
        <VerifyEmailBanner />
        <SimulatedSendsBanner />
        <Outlet />
      </main>
      {/* First-login walkthrough overlay — shows once per user (account-type aware). */}
      <Walkthrough />
    </div>
  );
}

// Collapsed width of the icon-only nav rail. Expanded uses --sidebar-width.
const NAV_COLLAPSED_W = 64;

function Sidebar({ onLogout }) {
  const { user, org, role } = useAuth();
  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase() || '?';
  const roleLabel = role ? (ROLE_LABELS[role] || role.charAt(0).toUpperCase() + role.slice(1)) : '';
  // Collapse state persists per-device (like the MLS filter rail / default-view choice).
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('feebe_nav_collapsed') === '1'; } catch { return false; }
  });
  const toggle = () => setCollapsed(c => {
    const next = !c;
    try { localStorage.setItem('feebe_nav_collapsed', next ? '1' : '0'); } catch {}
    return next;
  });

  const navLinkStyle = ({ isActive }) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    justifyContent: collapsed ? 'center' : 'flex-start',
    padding: collapsed ? '10px 0' : '9px 12px',
    borderRadius: 8, marginBottom: 2,
    fontSize: 14, fontWeight: isActive ? 600 : 400,
    color: isActive ? 'var(--teal)' : 'var(--gray-300)',
    background: isActive ? 'var(--teal-dim)' : 'transparent',
    transition: 'all 0.15s',
  });
  const SectionLabel = ({ children, first = false }) => collapsed ? (
    <div style={{ height: 1, background: 'var(--navy-border)', margin: first ? '4px 10px 10px' : '16px 10px 10px' }} />
  ) : (
    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: first ? '0 12px 8px' : '20px 12px 8px' }}>{children}</div>
  );

  return (
    <nav style={{
      width: collapsed ? NAV_COLLAPSED_W : 'var(--sidebar-width)',
      minWidth: collapsed ? NAV_COLLAPSED_W : 'var(--sidebar-width)',
      background: 'var(--navy-card)',
      borderRight: '1px solid var(--navy-border)',
      display: 'flex',
      flexDirection: 'column',
      padding: '0',
      transition: 'width 0.18s ease, min-width 0.18s ease',
    }}>
      {/* Logo + collapse toggle */}
      <div style={{
        padding: collapsed ? '20px 0 16px' : '28px 24px 20px',
        borderBottom: '1px solid var(--navy-border)',
      }}>
        {collapsed ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <img src="/feebe-mark-pin.png" alt="feebe" width="30" height="30" style={{ objectFit: 'contain' }} />
            <NotificationBell />
            <NavToggle collapsed onClick={toggle} />
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/feebe-mark-pin.png" alt="feebe" width="32" height="32" style={{ flexShrink: 0, objectFit: 'contain' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.03em' }}>
                <span style={{ color: 'var(--teal)' }}>fee</span><span style={{ color: 'var(--gold)' }}>be</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{org?.name || ''}</div>
            </div>
            <NotificationBell />
            <NavToggle onClick={toggle} />
          </div>
        )}
      </div>

      {/* Nav */}
      <div style={{ flex: 1, padding: collapsed ? '16px 10px' : '16px 12px', overflowY: 'auto', overflowX: 'hidden' }}>
        <SectionLabel first>Navigation</SectionLabel>
        {navFor(role).map(item => (
          <NavLink key={item.to} to={item.to} end={item.to === '/'} title={collapsed ? item.label : undefined} style={navLinkStyle}>
            <span style={{ opacity: 0.85 }}>{item.icon}</span>
            {!collapsed && item.label}
          </NavLink>
        ))}

        <ListsNav collapsed={collapsed} />

        {/* Platform-admin only — gated by the is_platform_admin flag from /api/auth/me. */}
        {user?.is_platform_admin && (
          <>
            <SectionLabel>Platform</SectionLabel>
            <NavLink to="/admin" title={collapsed ? 'Admin & Spend' : undefined} style={navLinkStyle}>
              <span style={{ opacity: 0.85 }}><SettingsIcon /></span>
              {!collapsed && <>Admin &amp; Spend</>}
            </NavLink>
          </>
        )}

        <SectionLabel>Tools</SectionLabel>
        {[
          { icon: <AIIcon />, label: 'AI Underwriting' },
          { icon: <MatchIcon />, label: 'Buyer Matching' },
          { icon: <PacketIcon />, label: 'Deal Packets' },
        ].map(t => (
          <div key={t.label} title={collapsed ? `${t.label} — access via deal row actions` : undefined} style={{
            padding: collapsed ? '10px 0' : '9px 12px', borderRadius: 8, marginBottom: 2, fontSize: 13,
            color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 10,
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}>
            {t.icon}
            {!collapsed && t.label}
          </div>
        ))}
        {!collapsed && (
          <div style={{ fontSize: 11, color: 'var(--gray-500)', padding: '4px 12px 0', lineHeight: 1.4 }}>
            Access via deal row actions
          </div>
        )}
      </div>

      {/* User / Logout */}
      <div style={{
        padding: collapsed ? '14px 10px' : '16px 12px',
        borderTop: '1px solid var(--navy-border)',
      }}>
        {collapsed ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div title={`${user?.name || user?.email || '—'}${roleLabel ? ` · ${roleLabel}` : ''}`} style={{
              width: 30, height: 30, borderRadius: '50%', background: 'var(--teal)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: '#FAF7F2',
            }}>{initial}</div>
            <button onClick={onLogout} title="Sign Out" aria-label="Sign Out" style={{
              width: 34, height: 34, borderRadius: 8, background: 'transparent',
              border: '1px solid var(--navy-border)', color: 'var(--gray-500)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
              onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)'; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--navy-border)'; e.currentTarget.style.color = 'var(--gray-500)'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, marginBottom: 4 }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%', background: 'var(--teal)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, color: '#FAF7F2', flexShrink: 0,
              }}>{initial}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name || user?.email || '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{roleLabel}</div>
              </div>
            </div>
            <button
              onClick={onLogout}
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 8,
                background: 'transparent', border: '1px solid var(--navy-border)',
                color: 'var(--gray-500)', fontSize: 13, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseOver={e => { e.target.style.borderColor = 'var(--red)'; e.target.style.color = 'var(--red)'; }}
              onMouseOut={e => { e.target.style.borderColor = 'var(--navy-border)'; e.target.style.color = 'var(--gray-500)'; }}
            >
              Sign Out
            </button>
          </>
        )}
      </div>
    </nav>
  );
}

// Small chevron button that collapses / expands the main nav rail.
function NavToggle({ collapsed = false, onClick }) {
  return (
    <button
      onClick={onClick}
      title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      style={{
        width: 26, height: 26, flexShrink: 0, borderRadius: 7, cursor: 'pointer',
        background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-500)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onMouseOver={e => { e.currentTarget.style.color = 'var(--teal)'; e.currentTarget.style.borderColor = 'var(--teal)'; }}
      onMouseOut={e => { e.currentTarget.style.color = 'var(--gray-500)'; e.currentTarget.style.borderColor = 'var(--navy-border)'; }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {collapsed ? <polyline points="9 18 15 12 9 6"/> : <polyline points="15 18 9 12 15 6"/>}
      </svg>
    </button>
  );
}

function ListsNav({ collapsed = false }) {
  const { lists } = useLists();
  const navigate = useNavigate();
  return (
    <>
      {collapsed ? (
        <div style={{ height: 1, background: 'var(--navy-border)', margin: '16px 10px 10px' }} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 12px 8px' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Lists</span>
          <span onClick={() => navigate('/lists')} title="Manage lists" style={{ cursor: 'pointer', color: 'var(--gray-500)', display: 'flex' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </span>
        </div>
      )}
      <NavLink to="/lists" end title={collapsed ? 'All Lists' : undefined} style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2, borderRadius: 8,
        justifyContent: collapsed ? 'center' : 'flex-start',
        padding: collapsed ? '10px 0' : '9px 12px',
        fontSize: 14, fontWeight: isActive ? 600 : 400,
        color: isActive ? 'var(--teal)' : 'var(--gray-300)', background: isActive ? 'var(--teal-dim)' : 'transparent',
      })}>
        <span style={{ opacity: 0.85 }}><ListsIcon /></span>
        {!collapsed && 'All Lists'}
      </NavLink>
      {lists.map(l => (
        <NavLink key={l.id} to={`/lists/${l.id}`} title={collapsed ? `${l.name} (${l.count})` : undefined} style={({ isActive }) => ({
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2, borderRadius: 8,
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '9px 0' : '7px 12px 7px 16px',
          fontSize: 13, fontWeight: isActive ? 600 : 400,
          color: isActive ? 'var(--white)' : 'var(--gray-300)', background: isActive ? 'var(--navy-card)' : 'transparent',
        })}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: l.color || 'var(--teal)', flexShrink: 0 }} />
          {!collapsed && <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</span>}
          {!collapsed && <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{l.count}</span>}
        </NavLink>
      ))}
    </>
  );
}

function ListsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
  );
}

// Dashboard — a gauge. Curved and round on purpose: the two icons sit next to each other in the rail,
// so they need different SILHOUETTES, not different arrangements of the same square. A four-pane board
// was the first attempt and read as a near-twin of the Pipeline's grid at 16px.
function DashboardIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 18a8 8 0 1 1 16 0" />
      <line x1="12" y1="18" x2="16" y2="12.5" />
      <circle cx="12" cy="18" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Pipeline — a funnel. Wide at the top, narrow at the bottom: the shape of the thing it names, and
// triangular where the Dashboard gauge is round.
function PipelineIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 4h18l-7 8.6V19l-4 2v-8.4z" />
    </svg>
  );
}

function DealsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
    </svg>
  );
}

function CommsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

function OffMarketIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21V8l9-5 9 5v13"/><path d="M9 21V12h6v9"/>
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  );
}

function MlsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

function BuyersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}

function AIIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  );
}

function MatchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  );
}

function PacketIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
    </svg>
  );
}
